import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { saveNote } from "../services/dataService";
import { resubmitNote } from "../services/teamService";
import { logAuditEvent, AuditEvents } from "../services/auditService";
import { calculateNoteBilling } from "../services/billingCalculation";
import {
  clearRecoveryData,
  clearLocalStorageBackup,
} from "../services/sessionRecoveryService";
import { ArrowLeft, Copy, CheckCircle, Edit3, AlertTriangle, Mic, Plus, Send, X, RotateCcw } from "lucide-react";

function NoteSection({ label, content, editing, onEdit, onSave, onCancel, readOnly }) {
  const [editText, setEditText] = useState(content);

  if (editing && !readOnly) {
    return (
      <div style={{ background: "rgba(15,23,42,0.5)", borderRadius: 12, padding: 16, marginBottom: 12, border: "1px solid rgba(99,179,237,0.2)" }}>
        <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase mb-2">{label}</div>
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          className="w-full bg-transparent text-sm text-gray-300 leading-relaxed border-none outline-none resize-none"
          style={{ minHeight: 120 }}
        />
        <div className="flex gap-2 mt-3">
          <button className="px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }} onClick={() => onSave(editText)}>Save</button>
          <button className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-400" style={{ background: "rgba(99,179,237,0.08)" }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "rgba(15,23,42,0.5)", borderRadius: 12, padding: 16, marginBottom: 12, border: "1px solid rgba(99,179,237,0.06)" }}>
      <div className="flex justify-between items-center">
        <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase">{label}</div>
        {!readOnly && (
          <button className="text-[10px] text-blue-400 font-semibold flex items-center gap-1" onClick={onEdit}>
            <Edit3 size={10} /> EDIT
          </button>
        )}
      </div>
      <div className="text-sm text-gray-400 leading-relaxed mt-2">{content || "Not documented"}</div>
    </div>
  );
}

function formatNoteAsText(note) {
  if (!note?.header) return "";
  let text = `GENERAL PROGRESS NOTE — CLINICAL
Individual: ${note.header.individual || ""}
DOB: ${note.header.dob || ""}
Date: ${note.header.date || ""}
Clinician: ${note.header.clinician || ""}
Location: ${note.header.location || ""}
Site: ${note.header.site || ""}
Service: ${note.header.serviceType || ""}
Duration: ${note.header.duration || ""}

TODAY'S PRESENTING ISSUES:
${note.presenting || ""}

CURRENT LIVING SITUATION: ${note.livingStatus || "N/A"}
SUICIDE RISK: ${note.suicideRisk || "N/A"}
MEDICATION ADHERENCE: ${note.medicationAdherence || "N/A"}
MEDICAL CONDITIONS ADDRESSED: ${note.medicalConditions || "N/A"}
`;

  if (note.interventions?.length > 0) {
    text += `\nINTERVENTIONS DURING SESSION:\n${note.interventions.map((item) => `- ${item}`).join("\n")}\n`;
  }

  text += `
RESPONSE TO INTERVENTIONS:
${note.response || ""}

INDIVIDUAL'S PROGRESS:
${note.progress || ""}
`;

  if (note.selfRating) text += `Self-Rating: ${note.selfRating}\n`;
  if (note.proudOf) text += `Most Proud Of: "${note.proudOf}"\n`;

  text += `
SERVICE RECOMMENDATION / PLAN:
${note.servicePlan || ""}

BILLING: ${note.billingCode || ""} — ${note.billingLabel || ""} | ${note.billingUnits || ""}`;

  if (note.billingReimbursement) {
    text += ` | Est. Reimbursement: $${note.billingReimbursement}`;
  }

  return text;
}

export default function ReviewNotePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [editingSection, setEditingSection] = useState(null);
  const [copied, setCopied] = useState(false);

  const noteData = location.state?.note;
  const isReadOnly = location.state?.readOnly === true;
  const isRevision = location.state?.isRevision === true;
  const revisionNoteId = location.state?.noteId;
  const revisionReason = location.state?.revisionReason;
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addText, setAddText] = useState("");
  const [addSection, setAddSection] = useState("response");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  // If there's no note data at all, show an error
  if (!noteData || !noteData.header) {
    return (
      <div className="min-h-screen bg-surface-primary flex flex-col items-center justify-center px-8">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ background: "rgba(236,201,75,0.1)" }}>
          <AlertTriangle size={36} className="text-yellow-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-100 mb-2 text-center">No Note Data</h2>
        <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs mb-6">
          This note hasn't been generated yet. Go back and generate the note from the transcript first.
        </p>
        <button
          className="px-6 py-3 rounded-xl text-white font-semibold text-sm"
          style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
          onClick={() => navigate("/notes")}
        >
          Back to Notes
        </button>
      </div>
    );
  }

  const [note, setNote] = useState(noteData);

  // Calculate billing info for display (uses note duration + service type)
  const billingInfo = (() => {
    try {
      // If note already has calculated billing from SubmittedPage, show that
      if (note.billingReimbursement && note.billingUnitsNum) {
        return {
          code: note.billingCode,
          label: note.billingLabel,
          units: note.billingUnitsNum,
          rate: note.billingRate,
          totalReimbursement: note.billingReimbursement,
          unitType: note.billingUnitType || "time",
          warnings: note.billingWarnings || [],
          durationMinutes: note.durationMinutes,
        };
      }
      // Otherwise calculate from header
      return calculateNoteBilling({
        duration: note.header?.duration,
        serviceType: note.header?.serviceType,
        noteData: note,
      });
    } catch {
      return null;
    }
  })();

  const handleCopy = () => {
    navigator.clipboard.writeText(formatNoteAsText(note)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      try { logAuditEvent(AuditEvents.NOTE_COPIED, {}); } catch (e) {}
    });
  };

  const handleSubmit = async () => {
    // Attach billing info to note before submit
    if (billingInfo) {
      note.billingCode = billingInfo.code;
      note.billingLabel = billingInfo.label;
      note.billingUnits = `${billingInfo.units} unit${billingInfo.units !== 1 ? "s" : ""}`;
      note.billingReimbursement = billingInfo.totalReimbursement;
    }

    let saveSucceeded = false;
    try {
      await saveNote({
        sessionId: location.state?.sessionId || "local",
        individualId: location.state?.individualId || "local",
        status: "submitted",
        content: note,
        billingInfo: {
          code: billingInfo?.code || note.billingCode,
          units: billingInfo?.units || 0,
          rate: billingInfo?.rate || 0,
          reimbursement: billingInfo?.totalReimbursement || 0,
          unitType: billingInfo?.unitType || "time",
        },
        confidence: note.confidence,
        gpsLocation: location.state?.gpsLocation || null,
      });
      saveSucceeded = true;
    } catch (e) { /* offline — still navigate; recovery row stays */ }

    // Only clear the in-flight recovery row after the note is safely persisted.
    // If we're offline, leave it so the user can recover and resubmit later.
    if (saveSucceeded && location.state?.sessionId) {
      await clearRecoveryData(location.state.sessionId);
      clearLocalStorageBackup(location.state.sessionId);
    }

    navigate("/session/submitted", {
      state: {
        note,
        draftNoteId: location.state?.draftNoteId || null,
        individualId: location.state?.individualId || null,
        duration: location.state?.duration || 1800,
        aiGenerationTime: location.state?.aiGenerationTime || 25,
        transcript: location.state?.transcript || "",
      },
    });
  };

  // Continue Session — go back to recording with the same individual
  const handleContinueSession = () => {
    const header = note.header || {};
    navigate("/session/new/continue", {
      state: {
        individualName: header.individual || location.state?.individualName || "Individual",
        dob: header.dob || "",
        locationType: header.location || "Out-of-Clinic",
        location: header.site || "",
        serviceType: header.serviceType || location.state?.serviceType || "ICM Weekly Visit",
        knownMedications: location.state?.knownMedications || [],
        knownDiagnoses: location.state?.knownDiagnoses || [],
        existingTranscript: location.state?.transcript || "",
        existingNote: note,
        draftNoteId: location.state?.draftNoteId || null,
      },
    });
  };

  // Add to Note — append additional context to a specific section
  const handleAppendToNote = () => {
    if (!addText.trim()) return;

    const appendText = addText.trim();
    const updated = { ...note };

    if (addSection === "response") {
      updated.response = (updated.response || "") + "\n\n[Additional context added after session]: " + appendText;
    } else if (addSection === "presenting") {
      updated.presenting = (updated.presenting || "") + "\n\n[Additional context]: " + appendText;
    } else if (addSection === "interventions") {
      updated.interventions = [...(updated.interventions || []), "[Added post-session] " + appendText];
    } else if (addSection === "progress") {
      updated.progress = (updated.progress || "") + "\n\n[Additional context]: " + appendText;
    } else if (addSection === "servicePlan") {
      updated.servicePlan = (updated.servicePlan || "") + "\n\n[Additional context]: " + appendText;
    }

    setNote(updated);
    setAddText("");
    setShowAddPanel(false);
  };

  const sec = { background: "rgba(15,23,42,0.5)", borderRadius: 12, padding: 16, marginBottom: 12, border: "1px solid rgba(99,179,237,0.06)" };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #0f1729, #162033)", borderBottom: "1px solid rgba(99,179,237,0.1)" }}>
        <button className="flex items-center gap-3" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} className="text-gray-300" />
          <span className="text-lg font-bold text-gray-100">
            {isReadOnly ? "View Note" : "Review Note"}
          </span>
        </button>
        <button className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(99,179,237,0.1)" }} onClick={handleCopy}>
          {copied ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} className="text-blue-400" />}
        </button>
      </header>

      <div className="px-5 py-4 pb-8 stagger-children">
        {/* Status Badge */}
        {isReadOnly ? (
          <div className="flex items-center gap-2.5 p-3 rounded-xl mb-4" style={{ background: "rgba(72,187,120,0.06)", border: "1px solid rgba(72,187,120,0.15)" }}>
            <CheckCircle size={16} className="text-green-400 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-green-400">Submitted Note</div>
              <div className="text-[11px] text-gray-500 mt-0.5">This note has been finalized · AI confidence: {note.confidence || "—"}%</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 p-3 rounded-xl mb-4" style={{ background: "rgba(236,201,75,0.06)", border: "1px solid rgba(236,201,75,0.15)" }}>
            <Edit3 size={16} className="text-yellow-400 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-yellow-400">Review Before Submitting</div>
              <div className="text-[11px] text-gray-500 mt-0.5">AI confidence: {note.confidence || "—"}% · Edit any section, then approve</div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={sec}>
          <div className="text-[11px] font-bold tracking-[1.5px] uppercase mb-2" style={{ color: "#63b3ed" }}>General Progress Note — Clinical</div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              ["Individual", note.header?.individual],
              ["DOB", note.header?.dob],
              ["Date", note.header?.date],
              ["Clinician", note.header?.clinician],
              ["Location", note.header?.location],
              ["Duration", note.header?.duration],
            ].map(([l, v], i) => (
              <div key={i}>
                <div className="text-[10px] text-gray-600 font-semibold uppercase tracking-wide">{l}</div>
                <div className="text-sm text-gray-300 mt-0.5">{v || "—"}</div>
              </div>
            ))}
          </div>
          {note.header?.site && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-600 font-semibold uppercase tracking-wide">Site</div>
              <div className="text-sm text-gray-300 mt-0.5">{note.header.site}</div>
            </div>
          )}
        </div>

        {/* Presenting */}
        <NoteSection label="Today's Presenting Issues" content={note.presenting} readOnly={isReadOnly} editing={editingSection === "presenting"} onEdit={() => setEditingSection("presenting")} onSave={(t) => { setNote({ ...note, presenting: t }); setEditingSection(null); }} onCancel={() => setEditingSection(null)} />

        {/* Status */}
        <div style={sec}>
          <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase">Status Fields</div>
          <div className="flex flex-col gap-2.5 mt-2">
            {[
              ["Current Living Situation", note.livingStatus],
              ["Suicide Risk", note.suicideRisk],
              ["Medication Adherence", note.medicationAdherence],
              ["Medical Conditions Addressed", note.medicalConditions],
            ].map(([l, v], i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{l}</span>
                <span className="text-sm font-semibold" style={{ color: v === "No" ? "#68d391" : v === "Yes" ? "#63b3ed" : v === "N/A" ? "#718096" : "#cbd5e0" }}>{v || "N/A"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Interventions */}
        {note.interventions?.length > 0 && (
          <div style={sec}>
            <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase">Interventions</div>
            <div className="mt-2">
              {note.interventions.map((item, i) => (
                <div key={i} className="flex gap-2 py-1.5" style={{ borderBottom: i < note.interventions.length - 1 ? "1px solid rgba(99,179,237,0.04)" : "none" }}>
                  <div className="w-1 h-1 rounded-full bg-teal-400 mt-2 shrink-0" />
                  <span className="text-sm text-gray-400 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Response */}
        <NoteSection label="Response to Interventions" content={note.response} readOnly={isReadOnly} editing={editingSection === "response"} onEdit={() => setEditingSection("response")} onSave={(t) => { setNote({ ...note, response: t }); setEditingSection(null); }} onCancel={() => setEditingSection(null)} />

        {/* Progress */}
        <div style={sec}>
          <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase">Individual's Progress</div>
          <div className="text-sm text-gray-400 leading-relaxed mt-2">{note.progress || "Not documented"}</div>
          {(note.selfRating || note.proudOf) && (
            <>
              <div className="h-px my-3" style={{ background: "rgba(99,179,237,0.08)" }} />
              {note.selfRating && (
                <div className="flex justify-between"><span className="text-xs text-gray-500">Self-Rating</span><span className="text-sm font-semibold text-green-400">{note.selfRating}</span></div>
              )}
              {note.proudOf && (
                <div className="flex justify-between mt-1.5"><span className="text-xs text-gray-500">Most Proud Of</span><span className="text-sm font-semibold text-teal-400 italic">"{note.proudOf}"</span></div>
              )}
            </>
          )}
        </div>

        {/* Service Plan */}
        <NoteSection label="Service Recommendation / Plan" content={note.servicePlan} readOnly={isReadOnly} editing={editingSection === "servicePlan"} onEdit={() => setEditingSection("servicePlan")} onSave={(t) => { setNote({ ...note, servicePlan: t }); setEditingSection(null); }} onCancel={() => setEditingSection(null)} />

        {/* ─── Enhanced Billing Section ─── */}
        <div style={{ ...sec, background: "rgba(79,209,197,0.04)", border: "1px solid rgba(79,209,197,0.1)" }}>
          <div className="text-[11px] font-bold tracking-[1.5px] uppercase mb-3" style={{ color: "#4fd1c5" }}>Billing Information</div>

          {billingInfo ? (
            <>
              <div className="flex justify-between py-1" style={{ borderBottom: "1px solid rgba(79,209,197,0.06)" }}>
                <span className="text-xs text-gray-500">Code</span>
                <span className="text-sm font-bold text-teal-400">{billingInfo.code}</span>
              </div>
              <div className="flex justify-between py-1" style={{ borderBottom: "1px solid rgba(79,209,197,0.06)" }}>
                <span className="text-xs text-gray-500">Service</span>
                <span className="text-xs font-semibold text-gray-300">{billingInfo.label || note.header?.serviceType || "—"}</span>
              </div>
              <div className="flex justify-between py-1" style={{ borderBottom: "1px solid rgba(79,209,197,0.06)" }}>
                <span className="text-xs text-gray-500">Duration</span>
                <span className="text-sm font-semibold text-gray-300">{billingInfo.durationMinutes || "—"} min</span>
              </div>
              <div className="flex justify-between py-1" style={{ borderBottom: "1px solid rgba(79,209,197,0.06)" }}>
                <span className="text-xs text-gray-500">Units</span>
                <span className="text-sm font-bold text-teal-400">
                  {billingInfo.units} {billingInfo.unitType === "encounter" ? "(encounter)" : `× ${billingInfo.rate ? "$" + billingInfo.rate.toFixed(2) : "—"}/unit`}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-xs text-gray-500">Est. Reimbursement</span>
                <span className="text-sm font-bold text-green-400">${billingInfo.totalReimbursement?.toFixed(2) || "0.00"}</span>
              </div>

              {/* Billing warnings */}
              {billingInfo.warnings?.length > 0 && (
                <div className="mt-2 pt-2" style={{ borderTop: "1px solid rgba(236,201,75,0.1)" }}>
                  {billingInfo.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-1.5 mt-1">
                      <AlertTriangle size={10} className="text-yellow-400 mt-0.5 shrink-0" />
                      <span className="text-[11px] text-yellow-400 leading-relaxed">{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-between"><span className="text-xs text-gray-500">Code</span><span className="text-sm font-bold text-teal-400">{note.billingCode || "—"}</span></div>
              <div className="flex justify-between mt-1.5"><span className="text-xs text-gray-500">Units</span><span className="text-sm font-semibold text-gray-300">{note.billingUnits || "—"}</span></div>
            </>
          )}

          {/* GPS/EVV */}
          {location.state?.gpsLocation && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(79,209,197,0.08)" }}>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Location Verified</span>
                <span className="text-[10px] font-mono text-teal-400">
                  ✓ {location.state.gpsLocation.latitude.toFixed(4)}, {location.state.gpsLocation.longitude.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">Accuracy</span>
                <span className="text-[10px] text-gray-400">±{location.state.gpsLocation.accuracy}m</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">Captured</span>
                <span className="text-[10px] text-gray-400">{new Date(location.state.gpsLocation.timestamp).toLocaleTimeString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {isReadOnly ? (
          <div className="flex flex-col gap-2.5 mt-5">
            <button className="w-full py-3.5 rounded-xl font-semibold text-base" style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }} onClick={handleCopy}>
              {copied ? "Copied!" : "Copy to Clipboard (for EHR)"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5 mt-5">
            {/* Revision banner */}
            {isRevision && revisionReason && (
              <div className="p-3 rounded-xl mb-1" style={{ background: "rgba(246,173,85,0.06)", border: "1px solid rgba(246,173,85,0.15)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <RotateCcw size={12} className="text-orange-400" />
                  <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Supervisor Revision Request</span>
                </div>
                <div className="text-xs text-gray-400 italic leading-relaxed">{revisionReason}</div>
              </div>
            )}

            {isRevision ? (
              <button
                className="w-full py-3.5 rounded-xl text-white font-semibold text-base"
                style={{ background: "linear-gradient(135deg, #dd6b20, #c05621)", opacity: resubmitting ? 0.5 : 1 }}
                disabled={resubmitting}
                onClick={async () => {
                  setResubmitting(true);
                  const agentName = (() => { try { return JSON.parse(localStorage.getItem("fieldnotes_user_profile") || "{}").displayName || "Agent"; } catch { return "Agent"; } })();
                  await resubmitNote(revisionNoteId, noteData, agentName);
                  setResubmitting(false);
                  navigate("/notes");
                }}
              >
                {resubmitting ? "Resubmitting..." : "Resubmit Revised Note"}
              </button>
            ) : (
              <button className="w-full py-3.5 rounded-xl text-white font-semibold text-base" style={{ background: "linear-gradient(135deg, #38a169, #2f855a)" }} onClick={handleSubmit}>
                Approve & Submit Note
              </button>
            )}
            <button className="w-full py-3.5 rounded-xl font-semibold text-base" style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }} onClick={handleCopy}>
              {copied ? "Copied!" : "Copy to Clipboard (for EHR)"}
            </button>

            {/* Continue / Add Options */}
            <div className="flex gap-2">
              <button
                className="flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
                style={{ background: "rgba(79,209,197,0.06)", color: "#4fd1c5", border: "1px solid rgba(79,209,197,0.15)" }}
                onClick={handleContinueSession}
              >
                <Mic size={14} /> Continue Session
              </button>
              <button
                className="flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
                style={{ background: "rgba(236,201,75,0.06)", color: "#ecc94b", border: "1px solid rgba(236,201,75,0.15)" }}
                onClick={() => setShowAddPanel(!showAddPanel)}
              >
                <Plus size={14} /> Add to Note
              </button>
            </div>
          </div>
        )}

        {/* Add to Note Panel */}
        {showAddPanel && (
          <div
            className="mt-3 rounded-2xl p-4"
            style={{ background: "rgba(236,201,75,0.04)", border: "1px solid rgba(236,201,75,0.12)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-yellow-400">Add Context to Note</div>
              <button onClick={() => setShowAddPanel(false)}>
                <X size={16} className="text-gray-600" />
              </button>
            </div>

            <div className="text-xs text-gray-500 mb-3">
              Add details you remember from the session. This will be marked as post-session additions.
            </div>

            {/* Section picker */}
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {[
                { key: "response", label: "Response" },
                { key: "presenting", label: "Presenting" },
                { key: "interventions", label: "Interventions" },
                { key: "progress", label: "Progress" },
                { key: "servicePlan", label: "Plan" },
              ].map((s) => (
                <button
                  key={s.key}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold"
                  style={{
                    background: addSection === s.key ? "rgba(236,201,75,0.15)" : "rgba(15,23,42,0.5)",
                    color: addSection === s.key ? "#ecc94b" : "#718096",
                    border: `1px solid ${addSection === s.key ? "rgba(236,201,75,0.3)" : "rgba(99,179,237,0.06)"}`,
                  }}
                  onClick={() => setAddSection(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <textarea
              className="w-full rounded-xl p-3 text-sm text-gray-300 leading-relaxed resize-none"
              style={{
                background: "rgba(15,23,42,0.5)",
                border: "1px solid rgba(236,201,75,0.1)",
                minHeight: 100,
                outline: "none",
              }}
              placeholder={
                addSection === "interventions"
                  ? "Describe additional intervention (e.g., 'Also reviewed housing application with Ind')"
                  : "Type additional context to append to this section..."
              }
              value={addText}
              onChange={(e) => setAddText(e.target.value)}
              autoFocus
            />

            <button
              className="w-full mt-3 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5"
              style={{
                background: addText.trim()
                  ? "linear-gradient(135deg, #b7791f, #975a16)"
                  : "rgba(236,201,75,0.1)",
                color: addText.trim() ? "white" : "#718096",
                opacity: addText.trim() ? 1 : 0.5,
              }}
              onClick={handleAppendToNote}
              disabled={!addText.trim()}
            >
              <Send size={14} />
              Add to {addSection === "interventions" ? "Interventions" : addSection === "presenting" ? "Presenting Issues" : addSection === "progress" ? "Progress" : addSection === "servicePlan" ? "Service Plan" : "Response"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}